<template>
  <div
    class="cartWrapper"
    :style="{ backgroundColor: branding.dark_mode_enabled ? 'var(--background-color)' : '#FFFFFF' }"
  >
    <Teleport to="body">
      <transition name="fade">
        <UpsellModal v-if="upsellModalIsOpen" @closeModal="toggleUpsellModal" />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <PhoneVerificationModal v-if="phoneVerificationModalIsOpen" @closeModal="togglePhoneVerificationModal" />
      </transition>
    </Teleport>

    <Teleport to="body">
      <transition name="fade">
        <EmailVerificationModal v-if="emailVerificationModalIsOpen" @closeModal="toggleEmailVerificationModal" />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <LoginModal
          v-if="loginModalIsOpen"
          @closeModal="toggleLoginModal"
          @signUpClicked="toggleSignUpModal"
          @forgotPasswordClicked="toggleForgotPasswordModal"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <SignUpModal
          v-if="signUpModalIsOpen"
          @closeModal="toggleSignUpModal"
          @loginClicked="toggleLoginModal"
          @accountCreated="togglePhoneVerificationModal"
          @openVerifyEmailModal="toggleEmailVerificationModal"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <ForgotPasswordModal
          v-if="forgotPasswordModalIsOpen"
          @closeModal="toggleForgotPasswordModal"
          @signUpClicked="toggleSignUpModal"
          @backToLogin="toggleLoginModal"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <MissingInfoModal
          v-if="missingInfoModalIsOpen"
          @closeModal="toggleMissingInfoModal"
          @openVerifyPhoneModal="togglePhoneVerificationModal"
          @openVerifyEmailModal="toggleEmailVerificationModal"
          @confirmedMissingInfo="checkoutClicked"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <GenericErrorModal
          v-if="genericErrorModalIsOpen"
          @closeModal="toggleGenericErrorModal"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <NeedMoreTimeModal
          v-if="needMoreTimeModalIsOpen"
          @closeModal="toggleNeedMoreTimeModal"
          @updateTime="updateOrderTime"
          :suggestedTime="orderData.order.suggested_ready_time_fmt"
          @confirmed="checkoutClicked"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <DateAndTimeModal
          v-if="dateAndTimeModalIsOpen"
          @closeModal="toggleDateAndTimeModal"
          :from="from"
          :message="orderData.order.errors?.need_more_time"
          @confirmedDate="checkoutClicked"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <RemoveItemModal
          v-if="removeItemModalIsOpen"
          @closeModal="toggleRemoveItemModal"
          :item="selectedItem"
          @removeItemRequest="removeItemClicked(selectedItem.order_item_id, selectedItem, index)"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <ClearCartModal
          v-if="clearCartModalIsOpen"
          @clearCart="handleClearCart"
          :clearCartButtonLoader="clearCartButtonLoader"
          @closeModal="toggleClearCartModal"
        />
      </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="fade">
        <TaxExempt
          v-if="taxExemptModalIsOpen"
          @closeModal="toggleTaxExemptModal"
          @taxExemptDecision="handleTaxExemptDecision"
          :confirmButtonLoader="confirmButtonLoader"
          :denyButtonLoader="denyButtonLoader"
        />
      </transition>
    </Teleport>
    <div v-if="orderDataLoading" class="loader"></div>
    <div
      v-else-if="!orderDataLoading && (!orderData || (orderData && orderData.order.items.length == 0))"
      class="emptyCart"
    >
      <div v-if="branding.display_options?.empty_cart?.enable" class="customEmpty">
        <img class="customEmptyImage" :src="branding.display_options?.empty_cart.image" alt="emptyCart" />
        <p>{{ branding.display_options?.empty_cart.text }}</p>
      </div>
      <div class="defaultEmpty" v-else>
        <svg class="cartImage" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0.265137 12.231C0.265137 13.6812 1.01221 14.4136 2.47705 14.4136H10.6289C11.896 14.4136 12.6357 13.6812 12.6357 12.231V5.06055C12.6357 
                  3.61768 11.8887 2.87793 10.4312 2.87793H9.36182C9.3252 1.39844 8.04346 0.138672 6.4541 0.138672C4.85742 0.138672 3.57568 
                  1.39844 3.53906 2.87793H2.47705C1.01221 2.87793 0.265137 3.61768 0.265137 5.06055V12.231ZM4.71826 2.87793C4.74756 1.98438 
                  5.47998 1.24463 6.4541 1.24463C7.4209 1.24463 8.15332 1.98438 8.18262 2.87793H4.71826ZM1.46631 12.1577V5.14111C1.46631 4.43799 
                  1.83984 4.0791 2.51367 4.0791H10.3872C11.061 4.0791 11.4419 4.43799 11.4419 5.14111V12.1577C11.4419 12.8608 11.061 13.2197 10.585 
                  13.2197H2.51367C1.83984 13.2197 1.46631 12.8608 1.46631 12.1577Z"
            fill="#1400FF"
          />
        </svg>
        <p>There are no items in your cart right now. Add items to get started.</p>
      </div>
    </div>
    <div v-else class="cartContent">
      <div class="scrollable">
        <TransitionGroup name="cartItems">
          <h2 v-if="windowWidth <= 1000" class="cartHeader" key="cartHeader">Your Cart</h2>
          <div
            class="orderInfo"
            v-if="
              selectedStore &&
              selectedService &&
              ($route.name != 'CheckoutView' || ($route.name == 'CheckoutView' && windowWidth <= 500))
            "
            key="orderInfo"
          >
            <div class="addressTop">
              <p class="serviceTypeInfo">
                {{ selectedService.display_name ? selectedService.display_name : selectedService.name }}
                {{ selectedService.requires_address ? 'to' : 'from' }}
              </p>
              <h3 v-if="selectedService.requires_address">
                {{ getSelectedAddress.address1
                }}<span style="font-family: var(--header-font)" v-if="getSelectedAddress.address2">{{
                  ', ' + getSelectedAddress.address2
                }}</span>
              </h3>
              <h3 v-if="!selectedService.requires_address">
                {{ selectedStore.name }}
              </h3>
              <p v-if="selectedService.requires_address">
                {{
                  getSelectedAddress.city + ', ' + getSelectedAddress.state_code + ', ' + getSelectedAddress.postal_code
                }}
              </p>
              <p v-if="!selectedService.requires_address && !branding.display_options.hide_store_address">
                {{ selectedStore.address1 + ', ' + selectedStore.state + ' ' + selectedStore.postal_code }}
              </p>
              <p v-if="selectedStore?.subtitle">{{ selectedStore.subtitle }}</p>
              <p v-if="orderData.order.guest_count !== null">Guest Count: {{ orderData.order.guest_count }}</p>
            </div>
            <div v-if="selectedService.requires_address" class="addressBottom">
              <p>From: {{ selectedStore.name }}</p>
            </div>
          </div>

          <div class="cartClear">
            <a @click="toggleClearCartModal()" href="javascript:;">
              <p class="tooltip">
                Clear Cart
                <span class="tooltiptext">Remove all items from your cart</span>
              </p>
            </a>
          </div>

          <div class="items" key="items">
            <TransitionGroup name="cartItems">
              <div
                v-for="(item, index) in orderData.order.items"
                :key="item.item_id"
                :id="item.item_id"
                class="item"
                :style="{ cursor: item.auto ? '' : 'pointer' }"
              >
                <div class="itemInfo">
                  <img
                    v-if="!item.auto && checkIfImageIsVisible"
                    class="itemImage"
                    :src="item.image_urls.thumbnail"
                    :alt="item.item_name"
                    loading="lazy"
                    @click="editButtonClicked(item)"
                  />
                  <div class="middle">
                    <div class="nameAndButtons">
                      <h4 @click="editButtonClicked(item)" class="itemName">
                        {{ item.item_name }}
                      </h4>
                      <h3 class="itemPrice">{{ calculateFullPrice(item) }}</h3>
                    </div>
                    <p class="specialNote" v-if="item.comment || item.comment != ''">Note: {{ item.comment }}</p>
                    <div class="modifiers">
                      <div v-if="item.size" class="modifier">
                        <p>{{ item.size.name }}</p>
                      </div>
                      <div v-if="item.style" class="modifier">
                        <p>{{ item.style.name }}</p>
                      </div>
                      <div v-for="modifier in getChildItems(item.child_items)" :key="modifier.item_id" class="modifier">
                        <p>
                          <span v-if="modifier?.pick_list_display_style === 'bulk' && modifier.quantity > 1">
                            {{ modifier.quantity }} X {{ modifier.item_name }}
                            <span v-if="modifier.extended_price != 0">
                              ({{ modifier.extended_price > 0 ? '+' : '' }}{{ formatPrice(modifier.extended_price) }})
                            </span>
                          </span>
                          <span v-else>
                            <span v-if="modifier.quantity / item.quantity > 1">
                              {{ modifier.quantity / item.quantity }} X
                            </span>
                            {{ modifier.item_name }}
                            <span v-if="modifier.extended_price != 0">
                              ({{ modifier.extended_price > 0 ? '+' : '' }}{{ formatPrice(modifier.extended_price) }})
                            </span>
                            <span v-if="modifier.style">({{ modifier.style.name }})</span>
                          </span>
                        </p>
                        <div class="subAddons">
                          <div
                            v-for="addon in getChildItems(modifier.child_items)"
                            :key="addon.item_id"
                            class="subAddon"
                          >
                            <p>
                              <span v-if="addon.quantity / modifier.quantity > 1"
                                >{{ addon.quantity / modifier.quantity }} X
                              </span>
                              {{ addon.item_name }}
                              <span v-if="addon.extended_price != 0"
                                >({{ addon.extended_price > 0 ? '+' : '' }}{{ formatPrice(addon.extended_price) }})
                              </span>
                              <span v-if="addon.style">({{ addon.style.name }})</span>
                            </p>
                            <div class="secondSubAddons">
                              <div
                                v-for="secondAddon in getChildItems(addon.child_items)"
                                :key="secondAddon.item_id"
                                class="secondSubAddon"
                              >
                                <p>
                                  <span v-if="secondAddon.quantity / addon.quantity > 1"
                                    >{{ secondAddon.quantity / addon.quantity }} X
                                  </span>
                                  {{ secondAddon.item_name }}
                                  <span v-if="secondAddon.extended_price != 0"
                                    >({{ secondAddon.extended_price > 0 ? '+' : ''
                                    }}{{ formatPrice(secondAddon.extended_price) }})
                                  </span>
                                  <span v-if="secondAddon.style">({{ secondAddon.style.name }})</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="!item.auto" class="qtyPriceLine">
                      <div class="buttons">
                        <a @click="editButtonClicked(item)" href="javascript:;">
                          <p>Modify</p>
                        </a>
                        <a @click="toggleRemoveItemModal(item)" href="javascript:;">
                          <p>Remove</p>
                        </a>
                      </div>
                      <div
                        :style="{
                          'border-color':
                            qtyLoading && selectedItemId == item.item_id && selectedItemIndex == index
                              ? 'var(--passive-color)'
                              : 'var(--primary-color)',
                          'background-color': branding.dark_mode_enabled ? 'var(--primary-color)' : 'var(--text-color)',
                        }"
                        class="qtyWrapper"
                        :class="{ qtyWrapperHover: showExpanded, 'not-allowed': isItemBulk(item) }"
                      >
                        <button
                          :disabled="
                            (qtyLoading && selectedItemId == item.item_id && selectedItemIndex == index) ||
                            isItemBulk(item)
                          "
                          @click="
                            item.quantity != item.min_qty
                              ? changeQty('minus', item, index)
                              : removeItemClicked(item.order_item_id, item, index)
                          "
                          :style="{
                            'border-color':
                              qtyLoading && selectedItemId == item.item_id && selectedItemIndex == index
                                ? 'var(--passive-color)'
                                : 'var(--text-color)',
                          }"
                          :class="{ qtyWrapperButtonHover: showExpanded, 'not-allowed': isItemBulk(item) }"
                        >
                          <template v-if="item.quantity != item.min_qty">-</template>
                          <svg
                            v-else
                            class="delete"
                            xmlns="http://www.w3.org/2000/svg"
                            width="9.998"
                            height="10.774"
                            viewBox="0 0 9.998 10.774"
                          >
                            <path
                              id="Delete_Solid"
                              d="M839.419,2646.771a.766.766,0,0,1-.767-.7l-.693-7.6c0-.025,0-.051,0-.076h-.574a.382.382,0,0,1,0-.764H839.9v-.866a.769.769,0,0,1,.768-.768h2.665a.769.769,0,0,1,.768.768v.866h2.518a.382.382,0,1,1,0,.764h-.574c0,.025,0,.051,0,.076l-.693,7.6a.766.766,0,0,1-.767.7Zm4.132-6.971a.379.379,0,0,0-.12.257l-.229,4.186a.382.382,0,0,0,.361.4h.021a.382.382,0,0,0,.381-.361l.229-4.186a.382.382,0,0,0-.36-.4h-.02a.381.381,0,0,0-.262.1Zm-1.933.278v4.186a.382.382,0,1,0,.764,0v-4.186a.382.382,0,1,0-.764,0Zm-1.451-.381a.382.382,0,0,0-.36.4l.229,4.186a.382.382,0,0,0,.381.361h.021a.382.382,0,0,0,.36-.4l-.229-4.186a.382.382,0,0,0-.382-.361Z"
                              transform="translate(-837.003 -2635.997)"
                            />
                          </svg>
                        </button>
                        <div class="qty">
                          <div
                            v-if="
                              (qtyLoading || removeItemLoading) &&
                              selectedItemId == item.item_id &&
                              selectedItemIndex == index
                            "
                            class="loader smallLoader"
                          ></div>
                          <span v-else>{{ item.quantity }}</span>
                        </div>
                        <button
                          :disabled="
                            (qtyLoading && selectedItemId == item.item_id && selectedItemIndex == index) ||
                            isItemBulk(item)
                          "
                          @click="changeQty('plus', item, index)"
                          :style="{
                            'border-color':
                              qtyLoading && selectedItemId == item.item_id && selectedItemIndex == index
                                ? 'var(--passive-color)'
                                : 'var(--text-color)',
                          }"
                          :class="{ qtyWrapperButtonHover: showExpanded, 'not-allowed': isItemBulk(item) }"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TransitionGroup>
          </div>
          <div
            v-if="
              orderData &&
              orderData.order_level_pick_list_data &&
              orderData.order_level_pick_list_data.pick_lists &&
              orderData.order_level_pick_list_data.pick_lists.length !== 0 &&
              upsellDisplayType === 'cart'
            "
            class="upsell cartUpsells"
            key="upsell"
          >
            <div v-for="upsell in orderData.order_level_pick_list_data.pick_lists" :key="upsell">
              <h3 class="upsellHeader" ref="upsellDropdown">
                {{ upsell.prompt }}
              </h3>
              <swiper
                :navigation="true"
                :slidesPerView="'auto'"
                :spaceBetween="10"
                :modules="modules"
                :slidesOffsetBefore="windowWidth > 500 ? 30 : 20"
                :slidesOffsetAfter="windowWidth > 500 ? 30 : 20"
                class="mySwiper"
              >
                <swiper-slide
                  v-for="item in upsell.pick_list_items"
                  :key="item.itemid"
                  class="upsellItem"
                  @click="openUpsellItem(item)"
                >
                  <img
                    v-if="checkIfImageIsVisible"
                    class="upsellItemImage"
                    :src="item.image_urls.standard"
                    :alt="item.name"
                    loading="lazy"
                  />

                  <RecommendedItem
                    :orderData="orderData"
                    :pickListItem="item"
                    :isPriceVisible="checkIfPriceVisible(item.baseprice)"
                  />
                </swiper-slide>
              </swiper>
            </div>
          </div>
          <div v-if="$route.name == 'CheckoutView'" class="totalPart" key="totalPart">
            <div v-if="orderData.order.charges.subtotal_cents != 0" class="totalLine">
              <p>Subtotal</p>
              <p>{{ formatPrice(orderData.order.charges.subtotal_cents) }}</p>
            </div>
            <div class="totalLine" v-if="this.orderData.order.charges.tip_cents != 0">
              <p>Tip</p>
              <p>{{ formatPrice(orderData.order.charges.tip_cents) }}</p>
            </div>
            <div v-if="orderData.order.charges.tax_cents != 0" class="totalLine">
              <p>Taxes</p>
              <p>{{ formatPrice(orderData.order.charges.tax_cents) }}</p>
            </div>
            <div v-if="orderData.order.charges.discount_cents != 0" class="totalLine">
              <p>Discount</p>
              <p>{{ formatPrice(orderData.order.charges.discount_cents) }}</p>
            </div>
            <div v-if="selectedService && selectedService.requires_address" class="totalLine">
              <p>Delivery Fee</p>
              <p>{{ formatPrice(orderData.order.charges.delivery_cents) }}</p>
            </div>
            <div v-if="orderData.order.charges.preauth_cents != 0" class="totalLine">
              <p>Paid</p>
              <p>{{ formatPrice(orderData.order.charges.preauth_cents) }}</p>
            </div>
            <div class="totalLine total">
              <p>Total</p>
              <p>{{ formatPrice(orderData.order.charges.total_cents) }}</p>
            </div>
            <div v-if="orderData.order.charges.other_paid_cents != 0" class="totalLine" style="margin-top: 15px">
              <p>Charged</p>
              <p>{{ formatPrice(orderData.order.charges.other_paid_cents) }}</p>
            </div>
            <div v-if="orderData.order.charges.other_paid_cents != 0" class="totalLine">
              <p>Remaining</p>
              <p>{{ formatPrice(orderData.order.charges.remaining_cents) }}</p>
            </div>
          </div>
        </TransitionGroup>
      </div>
      <div
        v-if="branding?.display_options.display_promos_and_rewards_on_cart !== false"
        class="promos"
        :style="{
          margin: $route.name == 'CheckoutView' && windowWidth > 500 ? '0px 30px' : '0',
          maxHeight: $route.name == 'CheckoutView' ? '300px' : '500px',
        }"
      >
        <div @click="togglePromos" class="promoLine">
          <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0.0244141 8.37869V2C0.0244141 1.18505 0.685046 0.524414 1.5 0.524414H7.87869C8.27004 0.524416 
                        8.64536 0.679881 8.92208 0.956607L15.5434 7.57792C16.1196 8.15417 16.1196 9.08846 15.5434 
                        9.66471L9.16471 16.0434C8.58846 16.6196 7.65417 16.6196 7.07792 16.0434L0.456607 9.42208C0.179881 
                        9.14536 0.0244161 8.77004 0.0244141 8.37869ZM3.5 2.47559C2.65808 2.47559 1.97559 3.15808 
                        1.97559 4C1.97559 4.84192 2.65808 5.52441 3.5 5.52441C4.34192 5.52441 5.02441 4.84192 5.02441 
                        4C5.02441 3.15808 4.34192 2.47559 3.5 2.47559Z"
              fill="black"
              stroke="black"
              stroke-width="0.0488281"
            />
          </svg>
          <h4>{{ storeConfig?.loyalty ? 'Promos and Rewards' : 'Promos' }}</h4>
          <svg
            :style="{
              transform: promosAreVisible ? 'rotate(180deg)' : 'unset',
            }"
            class="downArrow"
            width="12"
            height="9"
            viewBox="0 0 12 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.53558 7.93101L0.221517 2.61692C-0.0347764 2.36063 -0.0347764 1.94511 0.221517 1.68885L0.841317 1.06904C1.09717 0.813189 1.51184 0.812697 1.7683 1.06795L5.99963 5.27946L10.2309 1.06795C10.4874 0.812697 10.9021 0.813189 11.1579 1.06904L11.7777 1.68885C12.034 1.94514 12.034 2.36065 11.7777 2.61692L6.46368 7.93101C6.20739 8.18728 5.79188 8.18728 5.53558 7.93101Z"
              fill="#171717"
            />
          </svg>
        </div>
        <div class="promoContent" v-if="promosAreVisible">
          <div class="promoCode" v-if="!openConfirmLoyalty">
            <div class="promoCodeWrapper">
              <input v-model="promoCode" placeholder="Enter a promo code" type="text" />
              <button @click="redeemPromoCode(promoCode, 'redeem')">
                <div v-if="redeemLoading" class="loader buttonLoader"></div>
                <template v-if="!redeemLoading">Apply</template>
              </button>
            </div>
            <p v-if="v$.promoCode.$error === true" class="validation">Please enter a promo code</p>
            <p class="feedback error" v-if="error.status" v-html="error.message"></p>
          </div>

          <div class="rewards">
            <!-- Applied Coupons -->
            <div v-if="orderData.coupons && orderData.coupons.length > 0 && !openConfirmLoyalty" class="active">
              <h4 class="promoSectionHeader">Applied Discounts</h4>
              <div
                v-for="code in orderData.coupons"
                :key="code.clu"
                class="rewardItem selected"
                :class="{ selected: selectedReward }"
              >
                <div class="rewardContent">
                  <div class="rewardHeader">
                    <h4>
                      {{ code.name }}
                      <span class="chip">Applied</span>
                    </h4>
                    <button @click="removeDiscount(code)">
                      <template v-if="removeLoading && selectedCouponId === code.coupon_id">
                        <div class="loader smallLoader"></div>
                      </template>
                      <template v-else>Remove</template>
                    </button>
                  </div>
                  <p class="congratulationText">
                    Congratulation, you’ll get discount of {{ getSavedAmount(code.applied_value) }} in your cart.
                  </p>
                </div>
              </div>
            </div>
            <!-- Confirm Loyalty Modal -->
            <div v-if="loggedIn && openConfirmLoyalty" class="rewards">
              <div class="removeDiscount">
                <p class="loyaltyApplyText">
                  Your {{ selectedReward?.points }} loyalty points will be used to claim this exclusive offer.
                </p>
                <div class="buttonContainer">
                  <button @click="closeConfirmLoyalty()" class="cancelButton">Cancel</button>
                  <button @click="redeemPromoCode(selectedReward?.discount_code, 'apply')" class="confirmButton">
                    <div v-if="rewardLoading" class="loader buttonLoader"></div>
                    <template v-if="!rewardLoading">Confirm</template>
                  </button>
                </div>
              </div>
            </div>
            <!-- Rewards (always shown if available) -->
            <div v-if="loggedIn && !openConfirmLoyalty" class="rewards">
              <!-- Loyalty Rewards -->
              <div v-if="bankRewards.length > 0">
                <div class="rewardsContainer">
                  <svg
                    class="rewardsIcon"
                    width="31"
                    height="31"
                    viewBox="0 0 31 31"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_4401_1702)">
                      <path
                        d="M14 27.7846C21.613 27.7846 27.7846 21.613 27.7846 13.9999C27.7846 6.38691 21.613 0.215332 14 0.215332C6.38697 0.215332 0.215385 6.38691 0.215385 13.9999C0.215385 21.613 6.38697 27.7846 14 27.7846Z"
                        fill="var(--primary-color)"
                      />
                      <path
                        d="M21.2585 13.1601C21.7969 12.6216 21.4954 11.6954 20.7415 11.5877L17.1231 11.0708C16.8215 11.0277 16.5631 10.8339 16.4339 10.5754L14.8185 7.30159C14.4739 6.61236 13.5046 6.61236 13.1815 7.30159L11.5662 10.5754C11.4369 10.8554 11.1785 11.0277 10.8769 11.0708L7.25847 11.5877C6.50462 11.6954 6.20308 12.6216 6.74154 13.1601L9.3477 15.7231C9.56308 15.9385 9.67077 16.2401 9.60616 16.5416L8.98154 20.1385C8.87385 20.8924 9.67077 21.4524 10.3385 21.1077L13.5692 19.4062C13.8277 19.2554 14.1508 19.2554 14.4308 19.4062L17.6615 21.1077C18.3292 21.4524 19.1262 20.8924 18.9969 20.1385L18.3723 16.5416C18.3292 16.2401 18.4154 15.9385 18.6308 15.7231L21.2585 13.1601Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_4401_1702">
                        <rect width="31" height="31" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>

                  <div class="rewardsText">
                    <h4>Get More for Your Loyalty</h4>
                    <p>
                      You have <span class="loyaltyPoints">{{ currentBalance }}</span> loyalty points to unlock
                      exclusive rewards.
                    </p>
                  </div>
                </div>
                <div v-for="reward in bankRewards" :key="reward.id" class="rewardItem">
                  <div class="rewardContent">
                    <div class="rewardHeader">
                      <h4 v-if="reward?.name">{{ reward?.name || reward?.discount_code }}</h4>
                      <button
                        v-if="reward?.points <= currentBalance && !isAnyBankRewardUsed"
                        @click="toggleConfirmLoyalty(reward)"
                      >
                        Claim
                      </button>
                      <svg
                        v-else
                        width="31"
                        height="31"
                        viewBox="0 0 31 31"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22.1667 11.6666H23.3333C23.9777 11.6666 24.5 12.1889 24.5 12.8333V24.4999C24.5 25.1443 23.9777 25.6666 23.3333 25.6666H4.66667C4.02234 25.6666 3.5 25.1443 3.5 24.4999V12.8333C3.5 12.1889 4.02234 11.6666 4.66667 11.6666H5.83333V10.4999C5.83333 5.9896 9.48968 2.33325 14 2.33325C18.5103 2.33325 22.1667 5.9896 22.1667 10.4999V11.6666ZM19.8333 11.6666V10.4999C19.8333 7.27826 17.2216 4.66659 14 4.66659C10.7783 4.66659 8.16667 7.27826 8.16667 10.4999V11.6666H19.8333ZM12.8333 16.3333V20.9999H15.1667V16.3333H12.8333Z"
                          fill="var(--primary-color)"
                        />
                      </svg>
                    </div>
                    <p v-if="reward?.description" class="rewardDescription">{{ reward?.description }}</p>
                    <p v-if="reward?.points" class="claimText">Claim for {{ reward.points }} points</p>
                  </div>
                </div>
              </div>
              <!-- Non-Loyalty Rewards -->
              <div v-if="nonBankRewards.length > 0">
                <div class="rewardsContainer">
                  <svg
                    class="rewardsIcon"
                    width="31"
                    height="31"
                    viewBox="0 0 31 31"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_4389_2437)">
                      <path
                        d="M5.70802 27.9999H22.2909C23.4426 27.9999 24.3763 27.0662 24.3763 25.9146V15.4875H3.62271V25.9146C3.62271 27.0662 4.55641 27.9999 5.70802 27.9999Z"
                        fill="var(--primary-color)"
                      />
                      <path
                        d="M2.32669 11.6208V14.4198H25.6733V11.6208C25.6733 10.4692 24.7396 9.53549 23.588 9.53549H17.4838C20.3829 9.53549 24.5514 6.72188 23.6381 3.73832C22.8754 1.24585 20.5862 -0.014716 18.247 -0.000118809C14.9444 0.018649 14.0884 6.94241 14.0884 6.94241H13.9997C13.9997 6.94241 13.1442 0.018649 9.84111 -0.000118809C7.50139 -0.014716 5.21276 1.24585 4.45006 3.73832C3.53617 6.72188 7.70523 9.53549 10.6054 9.53549H4.412C3.26039 9.53549 2.32669 10.4692 2.32669 11.6208ZM20.6759 2.51633C20.6759 2.51633 20.5737 5.92373 15.9949 8.46833C15.9949 8.46833 16.8603 4.09387 20.6759 2.51633ZM7.45082 2.51633C11.2654 4.09335 12.1303 8.46833 12.1303 8.46833C7.55248 5.92373 7.45082 2.51633 7.45082 2.51633Z"
                        fill="var(--primary-color)"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_4389_2437">
                        <rect width="31" height="31" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <div class="rewardsText">
                    <h4>Available Rewards</h4>
                    <p>Don't miss out on your current available rewards</p>
                  </div>
                </div>
                <div v-for="reward in nonBankRewards" :key="reward.id" class="rewardItem">
                  <div class="rewardContent">
                    <div class="rewardHeader">
                      <div class="rewardTitles">
                        <h4 v-if="reward?.show_amount">{{ formatPrice(reward.amount) }}</h4>
                        <h4 v-if="reward?.name">{{ reward?.name || reward?.discount_code }}</h4>
                      </div>
                      <button @click="redeemPromoCode(reward.discount_code, 'apply')">
                        <div
                          v-if="applyLoading && selectedRewardId == reward.discount_code"
                          class="loader smallLoader"
                        ></div>
                        <template v-else>Apply</template>
                      </button>
                    </div>
                    <p v-if="reward?.description" class="rewardDescription">{{ reward?.description }}</p>
                    <div v-if="reward.expiration_dates && reward.expiration_dates.length > 0" class="expirationDates">
                      <p v-for="expiry in reward.expiration_dates" :key="expiry">
                        {{ expiry.show_amount ? formatPrice(expiry.amount) : '' }}
                        Expires {{ formatDate(expiry.date) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p v-if="!loggedIn && storeConfig?.loyalty" style="padding: 10px 20px 20px 20px">
              Please
              <span
                class="loginLink"
                :style="{ color: branding.dark_mode_enabled ? '#FFFFFF' : 'var(--primary-color)' }"
                @click="toggleLoginModal"
                >sign-in</span
              >
              to view rewards
            </p>
            <p
              v-if="loggedIn && (!availableRewards || availableRewards.length == 0)"
              style="padding: 10px 20px 20px 20px"
            >
              No available rewards
            </p>
          </div>
        </div>
      </div>
      <div v-if="$route.name != 'CheckoutView' && selectedStore" class="checkout">
        <div class="priceAndButton">
          <button
            class="checkoutButton"
            :disabled="checkMinimumAmount"
            :style="{
              'background-color': checkMinimumAmount ? 'var(--passive-color)' : 'var(--primary-color)',
              color: checkMinimumAmount ? '#FFFFFF' : 'var(--primary-text-color)',
            }"
            @click="checkoutClicked"
          >
            <div
              class="itemCount"
              :style="{ backgroundColor: branding.dark_mode_enabled ? 'var(--background-color)' : '#FFFFFF' }"
            >
              {{ getItemTotal }}
            </div>
            <div v-if="checkoutLoading" class="loader buttonLoader"></div>
            <template v-else>
              <span class="checkoutButton">
                Checkout •
                {{ formatPrice(orderData.order.charges.subtotal_cents + orderData.order.charges.discount_cents) }}
              </span>
            </template>
          </button>
        </div>
        <p v-if="checkMinimumAmount" class="minimumAmount">
          Minimum order amount for
          {{ selectedService.display_name ? selectedService.display_name : selectedService.name }}
          is {{ formatPrice(getMinimumAmount) }}. You are {{ formatPrice(getMinimumDifference) }} short.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import { mapState, mapMutations } from 'vuex'

import formattingMixins from '@/mixins/formattingMixins'
import menuItemImageVisibility from '@/mixins/menuItemImageVisibility'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper'
import 'swiper/css/navigation'
import 'swiper/css'

import PhoneVerificationModal from '@/components/Modals/AuthenticationModals/PhoneVerificationModal.vue'
import EmailVerificationModal from '@/components/Modals/AuthenticationModals/EmailVerificationModal.vue'
import LoginModal from '@/components/Modals/AuthenticationModals/LoginModal.vue'
import SignUpModal from '@/components/Modals/AuthenticationModals/SignUpModal.vue'
import ForgotPasswordModal from '@/components/Modals/AuthenticationModals/ForgotPasswordModal.vue'
import MissingInfoModal from '@/components/Modals/AuthenticationModals/MissingInfoModal.vue'
import GenericErrorModal from '@/components/Modals/StoreModals/GenericErrorModal.vue'
import NeedMoreTimeModal from '@/components/Modals/StoreModals/NeedMoreTimeModal.vue'
import DateAndTimeModal from '@/components/Modals/StoreModals/DateAndTimeModal.vue'
import UpsellModal from '@/components/Modals/StoreModals/UpsellModal.vue'
import RemoveItemModal from '@/components/Modals/ItemModals/RemoveItemModal.vue'
import ClearCartModal from '@/components/Modals/ItemModals/ClearCartModal.vue'
import TaxExempt from '@/components/Modals/StoreModals/TaxExempt.vue'
import RecommendedItem from '@/components/Elements/RecommendedItem.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import * as FullStory from '@fullstory/browser'

import ordersApi from '@/api/ordersApi'
import customersApi from '@/api/customersApi.js'

export default {
  setup() {
    return {
      v$: useVuelidate({ $stopPropagation: true }),
    }
  },
  data() {
    return {
      promosAreVisible: false,
      promoCode: '',
      qtyLoading: false,
      selectedItemId: null,
      selectedItemIndex: null,
      phoneVerificationModalIsOpen: false,
      emailVerificationModalIsOpen: false,
      availableRewards: null,
      redeemLoading: false,
      error: {
        status: false,
        message: '',
      },
      orderDataLoading: false,
      removeLoading: false,
      selectedCouponId: null,
      applyLoading: false,
      selectedRewardId: null,
      loginModalIsOpen: false,
      signUpModalIsOpen: false,
      forgotPasswordModalIsOpen: false,
      selectedItemData: null,
      selectedItemName: '',
      selectedUpsellIndex: 0,
      upsellDropdownIsVisible: false,
      missingInfoModalIsOpen: false,
      genericErrorModalIsOpen: false,
      needMoreTimeModalIsOpen: false,
      dateAndTimeModalIsOpen: false,
      removeItemModalIsOpen: false,
      clearCartModalIsOpen: false,
      taxExemptModalIsOpen: false,
      modalOpened: false,
      selectedItem: null,
      showExpanded: true,
      windowWidth: window.innerWidth,
      removeItemLoading: false,
      modules: [Navigation],
      from: null,
      checkoutLoading: false,
      upsellModalIsOpen: false,
      upsellModalControl: true,
      confirmButtonLoader: false,
      clearCartButtonLoader: false,
      denyButtonLoader: false,
      selectedReward: null,
      openConfirmLoyalty: false,
      currentBalance: 0,
      rewardLoading: false,
      guestCount: null,
    }
  },
  validations() {
    return {
      promoCode: {
        required,
      },
    }
  },
  mixins: [formattingMixins, menuItemImageVisibility],
  components: {
    Swiper,
    SwiperSlide,
    PhoneVerificationModal,
    EmailVerificationModal,
    LoginModal,
    SignUpModal,
    ForgotPasswordModal,
    MissingInfoModal,
    RemoveItemModal,
    ClearCartModal,
    NeedMoreTimeModal,
    DateAndTimeModal,
    GenericErrorModal,
    UpsellModal,
    RecommendedItem,
    TaxExempt,
  },
  watch: {
    orderData() {
      this.getAvailableDiscounts()
    },
    upsellModalIsOpen() {
      if (this.upsellModalIsOpen == false) {
        this.upsellModalControl = false
      }
    },
  },
  computed: {
    ...mapState([
      'orderData',
      'selectedDay',
      'selectedStoreDays',
      'selectedHour',
      'upSellItems',
      'selectedStore',
      'selectedService',
      'customerData',
      'loggedIn',
      'phoneVerified',
      'branding',
      'cookies',
      'storeConfig',
    ]),
    upsellDisplayType() {
      return this.branding?.display_options?.display_upsell_items_in_modal
    },
    checkIfMissingInfo() {
      if (!this.customerData?.first_name || this.customerData?.first_name == '') {
        return true
      }
      if (!this.customerData?.last_name || this.customerData?.last_name == '') {
        return true
      }
      if (this.storeConfig.require_phone_verification && (!this.customerData.phone || this.customerData.phone == '')) {
        return true
      }
      return false
    },
    notUsedAvailableRewards() {
      if (!this.availableRewards || !this.orderData.coupons) {
        return []
      }
      let rewards = this.availableRewards
      let usedRewards = this.orderData.coupons
      let notUsedRewards = rewards.filter((reward) => {
        let used = usedRewards.find((usedReward) => usedReward.clu == reward.discount_code)
        return !used
      })
      return notUsedRewards
    },
    bankRewards() {
      return this.notUsedAvailableRewards.filter((reward) => reward?.is_bank_reward)
    },
    nonBankRewards() {
      return this.notUsedAvailableRewards.filter(
        (reward) => reward?.is_bank_reward === false || reward?.is_bank_reward === undefined
      )
    },
    isAnyBankRewardUsed() {
      // Check if any bank reward is used
      if (!this.orderData || !this.orderData.coupons) {
        return false
      }
      return this.orderData.coupons.some(
        (coupon) => coupon.is_loyalty === true && coupon.clu && coupon.clu.startsWith('BANK-')
      )
    },
    getSelectedAddress() {
      let addressData = JSON.parse(localStorage.getItem('selectedAddress'))
      return addressData
    },
    checkMinimumAmount() {
      if (this.orderData.order.errors?.order_minimum) {
        return true
      } else {
        return false
      }
    },
    getMinimumAmount() {
      let selectedServiceId = localStorage.getItem('selectedServiceId')
      let selectedService = this.selectedStore.services.find((service) => service.service_type_id == selectedServiceId)
      let minimumAmount
      if (selectedService.requires_address) {
        let deliveryInfo = JSON.parse(localStorage.getItem('deliveryInfo'))
        minimumAmount = deliveryInfo.order_minimum
      } else {
        minimumAmount = selectedService.min_subtotal_cents
      }
      return minimumAmount
    },
    getMinimumDifference() {
      let minimumAmount = this.getMinimumAmount
      let subTotal = this.orderData.order.charges.subtotal_cents + this.orderData.order.charges.discount_cents
      let difference = minimumAmount - subTotal
      return difference
    },
    getItemTotal() {
      let itemQty = 0
      this.orderData.order.items.forEach((item) => {
        // Exclude auto items from the total
        if (!item.auto) itemQty += item.quantity
      })
      return itemQty
    },
  },
  methods: {
    ...mapMutations([
      'SET_ORDER_DATA',
      'SET_TOASTER',
      'SET_COOKIES',
      'SET_ITEM_TOASTER',
      'SET_BUTTON_LOADER',
      'SET_SELECTED_DAY',
      'SET_SELECTED_HOUR',
      'SET_DAY_AND_TIME_SELECTED',
    ]),
    checkIfPriceVisible(price) {
      if ('hide_price_if_zero' in this.branding?.display_options) {
        if (price == 0 && this.branding?.display_options?.hide_price_if_zero) {
          return false
        }
      }
      return true
    },
    formatDate(date) {
      return moment(date).format('MMM D, YYYY')
    },
    toggleLoginModal() {
      this.loginModalIsOpen = !this.loginModalIsOpen
    },
    toggleSignUpModal() {
      this.signUpModalIsOpen = !this.signUpModalIsOpen
    },
    toggleForgotPasswordModal() {
      this.forgotPasswordModalIsOpen = !this.forgotPasswordModalIsOpen
    },
    toggleUpsellDropdown() {
      this.upsellDropdownIsVisible = !this.upsellDropdownIsVisible
    },
    toggleMissingInfoModal() {
      this.missingInfoModalIsOpen = !this.missingInfoModalIsOpen
    },
    toggleNeedMoreTimeModal() {
      this.needMoreTimeModalIsOpen = !this.needMoreTimeModalIsOpen
    },
    toggleDateAndTimeModal() {
      this.dateAndTimeModalIsOpen = !this.dateAndTimeModalIsOpen
    },
    toggleRemoveItemModal(item) {
      this.error = {
        status: false,
        message: '',
      }
      this.selectedItem = item
      this.removeItemModalIsOpen = !this.removeItemModalIsOpen
    },
    toggleClearCartModal() {
      this.clearCartModalIsOpen = !this.clearCartModalIsOpen
    },
    toggleUpsellModal() {
      this.upsellModalIsOpen = !this.upsellModalIsOpen
    },
    changeUpsellList(index) {
      this.selectedUpsellIndex = index
      this.upsellDropdownIsVisible = false
    },
    getSavedAmount(amount) {
      let savedAmount = this.formatPrice(amount)
      // remove '-' character
      savedAmount = savedAmount.substring(1)
      return savedAmount
    },
    togglePromos() {
      this.promosAreVisible = !this.promosAreVisible
    },
    getChildItems(childItems) {
      // if should_show_in_cart is false, don't show the item
      if (!childItems) return
      let filteredChildItems = childItems.filter((childItem) => childItem.should_show_in_cart)
      return filteredChildItems
    },
    togglePhoneVerificationModal() {
      this.phoneVerificationModalIsOpen = !this.phoneVerificationModalIsOpen
    },
    toggleEmailVerificationModal() {
      this.emailVerificationModalIsOpen = !this.emailVerificationModalIsOpen
    },
    onSlideChange(e) {
      this.activeIndex = e.activeIndex
    },
    backButtonClicked() {
      this.$emit('closeCart')
    },
    calculateFullPrice(item) {
      let itemPrice = item.extended_price
      if (item.child_items) {
        item.child_items.forEach((childItem) => {
          itemPrice += childItem.extended_price
          if (childItem.child_items) {
            childItem.child_items.forEach((childItem) => {
              itemPrice += childItem.extended_price
              if (childItem.child_items) {
                childItem.child_items.forEach((childItem) => {
                  itemPrice += childItem.extended_price
                })
              }
            })
          }
        })
      }
      return this.formatPrice(itemPrice)
    },
    async getOrderDetails() {
      let needTimeError = {
        error: false,
        message: '',
        suggestedTime: null,
      }
      let orderId = localStorage.getItem('orderId')
      await ordersApi
        .getOrder(orderId)
        .then((response) => {
          this.orderDataLoading = false
          this.SET_ORDER_DATA(response.data.data)
          this.SET_COOKIES(response.data.cookies)
          if (response.data.data.order.errors && response.data.data.order.errors.need_more_time) {
            needTimeError = {
              error: true,
              message: response.data.data.order.errors.need_more_time,
              suggestedTime: response.data.data.order.suggested_ready_time_fmt,
            }
            return
          }
        })
        .catch((error) => {
          this.orderDataLoading = false
          console.log(error)
        })
      return needTimeError
    },
    getAvailableDiscounts() {
      let orderId = this.orderData.order.order_id
      ordersApi
        .getAvailableDiscounts(orderId)
        .then((response) => {
          let discounts = response.data.data.discounts.filter((discount) => discount.discount_code != null)
          this.availableRewards = discounts
          this.SET_COOKIES(response.data.cookies)
          if (this.loggedIn) {
            this.getLoyaltyInfo()
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getLoyaltyInfo() {
      let customerId = localStorage.getItem('customerId')
      customersApi
        .getLoyaltyInfo(customerId)
        .then((response) => {
          this.currentBalance =
            response?.data?.data?.info?.point_balances &&
            Array.isArray(response.data.data.info.point_balances) &&
            response.data.data.info.point_balances.length > 0
              ? response.data.data.info.point_balances[0].balance
              : 0
        })
        .catch((error) => {
          console.log(error)
        })
    },
    toggleConfirmLoyalty(code) {
      if (!code) {
        return
      }
      this.error = {
        status: false,
        message: '',
      }
      this.openConfirmLoyalty = !this.openConfirmLoyalty
      this.selectedReward = code
      this.trackClaimEvent(code)
    },
    trackClaimEvent(code) {
      const currentPage = this.$route.name || this.$route.path || 'Unknown'
      let pageLocation = 'Other'
      if (currentPage === 'CheckoutView') {
        pageLocation = 'Checkout'
      } else if (currentPage === 'MenuView') {
        pageLocation = 'Menu'
      }

      const chainName = localStorage.getItem('chainName')
      const customerId = localStorage.getItem('customerId')

      // Send event to FullStory using the imported object
      FullStory.event('claim_bank_reward', {
        reward_id: code.id,
        discount_name: code.name,
        order_id: this.orderData?.order?.order_id,
        tenant: chainName,
        user_id: customerId,
        page_location: pageLocation,
        platform: 'Web',
      })
    },
    closeConfirmLoyalty() {
      this.openConfirmLoyalty = false
      this.selectedReward = null
    },
    removeDiscount(code) {
      this.removeLoading = true
      this.selectedCouponId = code.coupon_id
      this.error = {
        status: false,
        message: '',
      }
      let orderId = this.orderData.order.order_id
      let customerId = this.loggedIn ? localStorage.getItem('customerId') : localStorage.getItem('guestCustomerId')
      let payload = {
        customer_id: parseInt(customerId),
        discount_id: parseInt(code.coupon_id),
      }
      ordersApi
        .removeDiscount(orderId, payload)
        .then((response) => {
          this.removeLoading = false
          this.selectedCouponId = null
          this.selectedReward = null
          this.openConfirmLoyalty = false
          this.SET_ORDER_DATA(response.data.data)
          this.SET_COOKIES(response.data.cookies)
          // Refresh loyalty balance after removing a loyalty reward
          if (this.loggedIn && (code.is_loyalty || code.is_bank_reward)) {
            this.getLoyaltyInfo()
          }
        })
        .catch((error) => {
          console.log(error)
          this.removeLoading = false
          this.selectedCouponId = null
          this.error = {
            status: false,
            message: '',
          }
        })
    },
    redeemPromoCode(code, from) {
      if (from == 'redeem') {
        this.v$.$touch()
        if (this.v$.$error) {
          return
        }
        this.redeemLoading = true
      }
      if (from == 'apply') {
        this.applyLoading = true
        this.rewardLoading = true
        this.selectedRewardId = code
      }
      this.error = {
        status: false,
        message: '',
      }
      let customerId = localStorage.getItem('customerId')
      let orderId = this.orderData.order.order_id
      let payload = {
        customer_id: this.loggedIn ? parseInt(customerId) : localStorage.getItem('guestCustomerId'),
        discount_code: code,
      }
      if (this.selectedReward && this.selectedReward.id) {
        payload.loyalty_coupon_code = this.selectedReward.id
      }
      ordersApi
        .redeemPromoCode(orderId, payload)
        .then((response) => {
          this.redeemLoading = false
          this.applyLoading = false
          this.rewardLoading = false
          this.SET_ORDER_DATA(response.data.data)
          this.SET_COOKIES(response.data.cookies)
          this.promoCode = ''
          this.v$.$reset()
          this.openConfirmLoyalty = false
          if (response.data.data.errors) {
            this.error = {
              status: true,
              message: response.data.data.errors[0],
            }
            this.selectedReward = null
          } else if (this.selectedReward && this.loggedIn) {
            // Refresh loyalty balance after successful reward redemption
            this.getLoyaltyInfo()
          }
        })
        .catch((error) => {
          console.log(error)
          this.redeemLoading = false
          this.applyLoading = false
          this.rewardLoading = false
          this.openConfirmLoyalty = false
          this.error = {
            status: false,
            message: '',
          }
        })
    },
    openUpsellItem(item) {
      this.$emit(
        'openUpsellItem',
        { item_id: item.itemid, menu_id: item.menuid, item_name: item.name, variants: [{ price: item.baseprice }] },
        { category_id: item.catid }
      )
    },
    removeItemClicked(itemId, itemData, index) {
      this.removeItemLoading = true
      this.SET_BUTTON_LOADER(true)
      this.selectedItemId = itemData.item_id
      this.error = {
        status: false,
        message: '',
      }
      let price = this.calculateFullPrice(itemData)
      price = price.replace(/\D/g, '')
      this.selectedItemData = {
        id: itemData.item_id,
        name: itemData.item_name,
        price: parseInt(price),
        quantity: itemData.quantity,
      }
      this.selectedItemIndex = index
      this.removeItem(itemId)
    },
    trackRemoveFromCart() {
     // Ensure window.dataLayer is initialized
     if (!window.dataLayer) {
        window.dataLayer = []
      }
      window.dataLayer.push({ ecommerce: null }) // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          currency: 'USD',
          value: this.selectedItemData.price / 100,
          items: [
            {
              item_id: this.selectedItemData.id,
              item_name: this.selectedItemData.name,
              price: this.selectedItemData.price / 100,
              quantity: this.selectedItemData.quantity,
              location_id: this.selectedStore.name,
            },
          ],
        },
      })
    },
    removeItem(itemId) {
      let orderId = this.orderData.order.order_id
      ordersApi
        .removeItemFromCart(orderId, itemId)
        .then((response) => {
          if (process.env.VUE_APP_GTM_ID || process.env.VUE_APP_GA4_ID) {
            // GTM track remove from cart
            this.trackRemoveFromCart()
          }
          this.SET_ORDER_DATA(response.data.data)
          let toasterData = {
            show: true,
            message: 'Item removed from cart',
          }
          this.removeItemLoading = false
          this.SET_BUTTON_LOADER(false)
          this.toggleRemoveItemModal()
          this.SET_ITEM_TOASTER(toasterData)
          this.SET_COOKIES(response.data.cookies)
        })
        .catch((error) => {
          this.removeItemLoading = false
          this.SET_BUTTON_LOADER(false)
          this.toggleRemoveItemModal()
          let toasterData = {
            show: true,
            message: 'Something went wrong. Please try again.',
          }
          this.SET_TOASTER(toasterData)
          console.log(error)
        })
    },
    changeQty(type, item, index) {
      // check min qty of the item
      if (item.min_qty) {
        if (type == 'minus' && item.quantity == item.min_qty) {
          let toasterData = {
            show: true,
            message: 'Minimum quantity you can select is ' + item.min_qty + ' for ' + item.item_name,
          }
          this.SET_TOASTER(toasterData)
          return
        }
      }
      // check max qty of the item
      if (item.max_qty) {
        if (type == 'plus' && item.quantity == item.max_qty) {
          let toasterData = {
            show: true,
            message: 'Maximum quantity you can select is ' + item.max_qty + ' for ' + item.item_name,
          }
          this.SET_TOASTER(toasterData)
          return
        }
      }
      this.qtyLoading = true
      this.selectedItemId = item.item_id
      this.selectedItemIndex = index
      if (type == 'minus' && item.quantity == 1) {
        this.removeItemClicked(item.order_item_id)
        return
      }
      let orderId = this.orderData.order.order_id
      let customerId = localStorage.getItem('customerId')
      let currentQty = item.quantity
      let newQty
      if (type == 'plus') {
        if (currentQty == 999) {
          this.qtyLoading = false
          let toasterData = {
            show: true,
            message: 'Maximum quantity you can select is 999 for ' + item.item_name,
          }
          this.SET_TOASTER(toasterData)
          return
        }
        newQty = currentQty + 1
      } else {
        newQty = currentQty - 1
      }
      let payload = {
        customer_id: this.loggedIn ? customerId : localStorage.getItem('guestCustomerId'),
        items: [this.generateOrderObject(item, 'changeQty')],
      }
      payload.items[0].quantity = newQty
      payload.items[0].order_item_id = item.order_item_id
      payload.items[0].child_items.forEach((childItem) => {
        let childItemQuantityForOne = childItem.quantity / currentQty
        if (type == 'plus') {
          childItem.quantity = childItemQuantityForOne * (currentQty + 1)
        } else {
          childItem.quantity = childItemQuantityForOne * (currentQty - 1)
        }
        if (childItem.child_items) {
          childItem.child_items.forEach((childChildItem) => {
            let childChildItemQuantityForOne = childChildItem.quantity / currentQty
            if (type == 'plus') {
              childChildItem.quantity = childChildItemQuantityForOne * (currentQty + 1)
            } else {
              childChildItem.quantity = childChildItemQuantityForOne * (currentQty - 1)
            }
            if (childChildItem.child_items) {
              childChildItem.child_items.forEach((childChildChildItem) => {
                let childChildChildItemQuantityForOne = childChildChildItem.quantity / currentQty
                if (type == 'plus') {
                  childChildChildItem.quantity = childChildChildItemQuantityForOne * (currentQty + 1)
                } else {
                  childChildChildItem.quantity = childChildChildItemQuantityForOne * (currentQty - 1)
                }
              })
            }
          })
        }
      })
      ordersApi
        .changeItemQuantity(orderId, payload)
        .then((response) => {
          this.qtyLoading = false
          this.selectedItemId = null
          this.selectedItemIndex = null
          this.SET_ORDER_DATA(response.data.data)
          this.SET_COOKIES(response.data.cookies)
        })
        .catch((error) => {
          console.log(error)
          this.qtyLoading = false
          this.selectedItemId = null
          this.selectedItemIndex = null
          let toasterData = {
            show: true,
            message: 'Something went wrong. Please try again later.',
          }
          this.SET_TOASTER(toasterData)
        })
    },
    openPhoneVerificationModal() {
      this.phoneVerificationModalIsOpen = true
    },
    openEmailVerificationModal() {
      this.emailVerificationModalIsOpen = true
    },
    toggleGenericErrorModal() {
      this.genericErrorModalIsOpen = !this.genericErrorModalIsOpen
    },
    toggleTaxExemptModal() {
      this.taxExemptModalIsOpen = !this.taxExemptModalIsOpen
    },
    handleTaxExemptDecision(decision) {
      if (decision.exempt) {
        this.confirmButtonLoader = true
        this.createOrder(true)
      } else {
        this.denyButtonLoader = true
        this.createOrder(false)
      }
      this.toggleTaxExemptModal()
    },
    handleClearCart() {
      this.clearCartButtonLoader = true
      let items = this.orderData.order.items.map((item) => {
        return {
          category_id: item.category_id,
          item_id: item.item_id,
          order_item_id: item.order_item_id,
          menu_id: item.menu_id,
          quantity: 0,
          comment: item.comment,
        }
      })

      let orderId = this.orderData.order.order_id
      let customerId = localStorage.getItem('customerId')
      let payload = {
        customer_id: this.loggedIn ? customerId : localStorage.getItem('guestCustomerId'),
        items: items,
      }
      ordersApi
        .addItemToCart(orderId, payload)
        .then((response) => {
          this.SET_ORDER_DATA(response.data.data)
          this.SET_COOKIES(response.data.cookies)
          let toasterData = {
            show: true,
            message: 'Cart cleared successfully',
          }
          this.SET_TOASTER(toasterData)
          this.clearCartButtonLoader = false
        })
        .catch((error) => {
          if (error.response.data.status == 403) {
            let toasterData = {
              show: true,
              message: 'Your session is expired. Please login again.',
            }
            this.SET_TOASTER(toasterData)
          } else {
            let toasterData = {
              show: true,
              message: 'Something went wrong. Please try again later.',
            }
            this.SET_TOASTER(toasterData)
          }
          this.clearCartButtonLoader = false
        })
    },
    async createOrder(isTaxExempt) {
      let serviceTypeId = localStorage.getItem('selectedServiceId')
      let storeId = localStorage.getItem('selectedStoreId')
      let orderId = this.orderData.order.order_id

      let payload = {
        customer_id: localStorage.getItem('customerId'),
        is_asap: this.selectedStoreDays[0].hours[0].hour == 'ASAP' ? true : false,
        store_id: storeId,
        service_type_id: parseInt(serviceTypeId),
        base_order_id: orderId,
        is_cc_tax_exempt: isTaxExempt,
      }
      if (this.selectedStoreDays[0].hours[0].hour != 'ASAP') {
        payload.ready_ts = this.selectedStoreDays[0].date + ' ' + this.selectedStoreDays[0].hours[0].hour
      }
      await ordersApi
        .createOrder(payload)
        .then((response) => {
          localStorage.setItem('orderId', response.data.data.order.order_id)
          this.SET_ORDER_DATA(response.data.data)
          localStorage.setItem('dayAndTimeSelected', true)
          this.SET_DAY_AND_TIME_SELECTED(true)
          this.SET_COOKIES(response.data.cookies)
        })
        .catch((error) => {
          console.log(error)
          this.SET_BUTTON_LOADER(false)
          let toasterData = {
            show: true,
            message: 'Something went wrong. Please try again',
          }
          this.SET_TOASTER(toasterData)
        })
        .finally(() => {
          this.confirmButtonLoader = false
          this.denyButtonLoader = false
          this.checkoutClicked()
        })
    },
    async checkoutClicked() {
      this.checkoutLoading = true
      let needTimeError = await this.getOrderDetails()
      let isTaxExempt = this.customerData?.is_tax_exempt
      if (this.loggedIn && isTaxExempt && !this.modalOpened) {
        this.checkoutLoading = false
        this.toggleTaxExemptModal()
        this.modalOpened = true
        return
      }

      // if suggested time is returned, show modal
      if (needTimeError.error && needTimeError.suggestedTime) {
        this.checkoutLoading = false
        this.toggleNeedMoreTimeModal()
        return
      }

      // if suggested time is not returned, show error
      if (needTimeError.error && !needTimeError.suggestedTime) {
        this.from = 'needMoreTime'
        this.checkoutLoading = false
        this.toggleDateAndTimeModal()
        return
      }

      if (!this.loggedIn && !this.storeConfig.allow_guest_orders) {
        this.checkoutLoading = false
        this.toggleLoginModal()
        return
      }
      if (this.loggedIn && this.checkIfMissingInfo) {
        this.checkoutLoading = false
        this.toggleMissingInfoModal()
        return
      }
      if (this.loggedIn && this.storeConfig.require_phone_verification && !this.customerData.is_phone_verified) {
        this.checkoutLoading = false
        this.openPhoneVerificationModal()
        return
      }
      if (this.loggedIn && this.storeConfig.require_email_verification && !this.customerData.is_verified) {
        this.checkoutLoading = false
        this.openEmailVerificationModal()
        return
      }

      if (this.orderData.order.errors && Object.keys(this.orderData.order.errors).length > 0) {
        this.checkoutLoading = false
        this.toggleGenericErrorModal()
        return
      }

      if (
        this.upsellDisplayType == 'modal' &&
        this.orderData?.order_level_pick_list_data?.pick_lists?.length > 0 &&
        this.upsellModalControl
      ) {
        this.checkoutLoading = false
        this.toggleUpsellModal()
        return
      }

      this.$emit('checkoutClicked')
      let storeId = localStorage.getItem('selectedStoreId')
      let storeNameAsSlug = this.selectedStore.name
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]+/g, '')
      this.checkoutLoading = false
      let isGuest = localStorage.getItem('isGuest')
      if (!this.loggedIn && this.storeConfig?.allow_registered_guest_checkout && isGuest) {
        this.$router.push({
          name: 'LoginView',
        })
      } else {
        this.$router.push({
          name: 'CheckoutView',
          params: { storeId: storeId, storeName: storeNameAsSlug },
        })
      }
    },
    editButtonClicked(item) {
      if (item.auto) return
      this.generateOrderObject(item, 'editClicked')
    },
    childItems(child_items) {
      let childItems = []
      if (!child_items || child_items.length == 0) {
        return []
      }
      child_items.forEach((child) => {
        let data = {
          menu_id: child.menu_id,
          size_id: child.item_size_id,
          style_id: child.item_style_id,
          quantity: child.quantity,
          price: child.extended_price / child.quantity,
          name: child.item_name,
          item_id: child.item_id,
          category_id: child.category_id,
          pick_list_id: child.pick_list_id,
          item_config_id: child.item_config_id,
          child_items: this.childItems(child.child_items),
        }
        if (!child.child_items) {
          delete data.child_items
        }
        childItems.push(data)
      })
      return childItems
    },
    generateOrderObject(item, action) {
      let itemObject = {
        menu_id: item.menu_id,
        size_id: item.item_size_id,
        style_id: item.item_style_id,
        quantity: item.quantity,
        item_id: item.item_id,
        category_id: item.category_id,
        comment: item.comment,
        child_items: this.childItems(item.child_items),
        order_item_id: item.order_item_id,
      }
      if (action == 'editClicked') {
        /* if (!item.child_items && !item.size) {
          this.$emit('openWithOutAddons', itemObject) 
        } */
        this.$emit('openWithAddons', itemObject)
      }
      if (action == 'changeQty') {
        return itemObject
      }
    },
    updateOrderTime() {
      let serviceTypeId = localStorage.getItem('selectedServiceId')
      let suggestedReadyTime = moment(this.orderData.order.suggested_ready_time)
        .utcOffset(this.orderData.order.utc_offset_minutes)
        .format('YYYY-MM-DD HH:mm:ss')
      let orderId = this.orderData.order.order_id
      let payload = {
        customer_id: this.loggedIn ? localStorage.getItem('customerId') : localStorage.getItem('guestCustomerId'),
        ready_ts: suggestedReadyTime,
        store_id: this.selectedStore.store_id,
        service_type_id: parseInt(serviceTypeId),
        order_id: parseInt(orderId),
        is_time_bump: true,
      }
      if (this.selectedService.group_name == 'catering' && this.guestCount) {
        payload.guest_count = this.guestCount
      }
      ordersApi
        .updateOrder(payload)
        .then((response) => {
          this.SET_COOKIES(response.data.cookies)
          this.toggleNeedMoreTimeModal()
          let formattedDate =
            moment(suggestedReadyTime).format('YYYY-MM-DD') == moment().format('YYYY-MM-DD')
              ? 'TODAY'
              : moment(suggestedReadyTime).format('YYYY-MM-DD') == moment().add(1, 'days').format('YYYY-MM-DD')
              ? 'TOMORROW'
              : moment(suggestedReadyTime).format('MMM D, YYYY')
          this.SET_SELECTED_DAY({
            date: moment(suggestedReadyTime).format('YYYY-MM-DD'),
            formatted: formattedDate,
          })
          localStorage.setItem(
            'selectedDay',
            JSON.stringify({
              date: moment(suggestedReadyTime).format('YYYY-MM-DD'),
              formatted: formattedDate,
            })
          )
          let formattedHour = moment(suggestedReadyTime).format('h:mm A')
          this.SET_SELECTED_HOUR({
            hour: moment(suggestedReadyTime).format('HH:mm'),
            formatted: formattedHour,
          })
          localStorage.setItem(
            'selectedHour',
            JSON.stringify({
              hour: moment(suggestedReadyTime).format('HH:mm'),
              formatted: formattedHour,
            })
          )
          let toasterData = {
            show: true,
            message: 'Order time updated',
          }
          this.SET_TOASTER(toasterData)
        })
        .catch((error) => {
          this.SET_BUTTON_LOADER(false)
          console.log(error)
        })
    },
    isItemBulk(item) {
      return item.child_items?.some((childItem) => childItem?.pick_list_display_style === 'bulk')
    },
  },
  created() {
    if (!this.orderData && localStorage.getItem('orderId')) {
      this.orderDataLoading = true
      this.getOrderDetails()
    }
    if (this.orderData) {
      this.getAvailableDiscounts()
    }
  },
  mounted() {
    this.guestCount = this.orderData?.order?.guest_count || null
    // show quantity as expanded for 2 seconds
    setTimeout(() => {
      this.showExpanded = false
    }, 2000)

    // Event handler functions stored as component properties
    this.handleClickOutside = (e) => {
      if (this.upsellDropdownIsVisible && this.$refs.upsellDropdown) {
        if (!this.$refs.upsellDropdown.contains(e.target)) {
          this.upsellDropdownIsVisible = false
        }
      }
    }

    this.handleResize = () => {
      this.windowWidth = window.innerWidth
    }

    // if upsellDropdown is open, close it when user clicks outside of it
    document.addEventListener('click', this.handleClickOutside)
    // add event listener for window resize
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    // remove event listeners
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside)
    }
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
  },
}
</script>

<style src="../../../assets/css/Main/Layout/CartView.css" scoped></style>
